/**
 * ReceiptQueue — the unassigned-receipt list on the Belege page
 * (`GET /api/receipts?status=unassigned`). Each row shows a thumbnail (or a
 * `FileText` placeholder for PDFs), whatever the extraction engine read off
 * the file, and an amber hint on `extraction_error`. "Zuordnen" opens the
 * candidate picker; "Löschen" goes through the same `ConfirmDialog` pattern
 * as `TransactionRow`'s `ReceiptRow`.
 */
import { useState } from 'react';
import { AlertTriangle, FileText, Paperclip, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/Button.tsx';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.tsx';
import { EmptyState } from '../../components/ui/EmptyState.tsx';
import { ErrorState } from '../../components/ui/ErrorState.tsx';
import { SkeletonList } from '../../components/ui/Skeleton.tsx';
import { useOnlineStatus } from '../../context/OnlineStatusContext.tsx';
import { useToast } from '../../context/ToastContext.tsx';
import { formatDate, money } from '../../lib/format.ts';
import { useDeleteReceipt, useReceipts } from '../../lib/hooks/useReceipts.ts';
import type { Receipt } from '../../lib/types.ts';

export interface ReceiptQueueProps {
  onZuordnen: (receipt: Receipt) => void;
}

export function ReceiptQueue({ onZuordnen }: ReceiptQueueProps) {
  const { guardOnline } = useOnlineStatus();
  const { toast } = useToast();
  const receiptsQuery = useReceipts('unassigned');
  const deleteMutation = useDeleteReceipt();
  const [pendingDelete, setPendingDelete] = useState<Receipt | null>(null);

  const receipts = receiptsQuery.data ?? [];

  function deleteReceipt() {
    if (!pendingDelete) return;
    if (!guardOnline()) return;
    deleteMutation.mutate(pendingDelete.id, {
      onSuccess: () => {
        setPendingDelete(null);
        toast('Beleg gelöscht.', { variant: 'success' });
      },
      onError: (error) =>
        toast(error instanceof Error ? error.message : 'Fehler beim Speichern.', {
          variant: 'error',
        }),
    });
  }

  if (receiptsQuery.isLoading) {
    return (
      <div className="py-2">
        <SkeletonList rows={3} />
      </div>
    );
  }

  if (receiptsQuery.isError) {
    return (
      <ErrorState
        title="Belege konnten nicht geladen werden"
        error={receiptsQuery.error}
        onRetry={() => void receiptsQuery.refetch()}
      />
    );
  }

  if (receipts.length === 0) {
    return (
      <EmptyState
        icon={Paperclip}
        title="Keine offenen Belege"
        description="Hochgeladene Belege ohne Zuordnung erscheinen hier."
      />
    );
  }

  return (
    <>
      <ul className="divide-y divide-slate-100 dark:divide-slate-800/70">
        {receipts.map((receipt) => (
          <li key={receipt.id} className="flex items-center gap-4 py-3 -mx-2 px-2">
            {receipt.mime_type === 'application/pdf' ? (
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md bg-slate-100 dark:bg-slate-800">
                <FileText className="h-6 w-6 text-slate-400 dark:text-slate-500" aria-hidden />
              </div>
            ) : (
              <img
                src={`/api/receipts/${receipt.id}/file`}
                alt={receipt.file_name}
                loading="lazy"
                className="h-16 w-16 shrink-0 rounded-md object-cover"
              />
            )}

            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                {receipt.file_name}
              </p>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-500 dark:text-slate-500">
                {receipt.extracted_merchant ? <span>{receipt.extracted_merchant}</span> : null}
                {receipt.extracted_amount ? (
                  <span className="tabular-nums">{money(receipt.extracted_amount)}</span>
                ) : null}
                {receipt.extracted_date ? (
                  <span className="tabular-nums">{formatDate(receipt.extracted_date)}</span>
                ) : null}
              </p>
              {receipt.extraction_error ? (
                <p className="mt-1 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
                  {receipt.extraction_error}
                </p>
              ) : null}
            </div>

            <div className="flex shrink-0 items-center gap-1">
              <Button variant="secondary" size="sm" onClick={() => onZuordnen(receipt)}>
                Zuordnen
              </Button>
              <button
                type="button"
                onClick={() => setPendingDelete(receipt)}
                aria-label="Beleg löschen"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition-colors duration-150 hover:bg-rose-50 hover:text-rose-600 dark:text-slate-500 dark:hover:bg-rose-950/50 dark:hover:text-rose-400"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </li>
        ))}
      </ul>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="Beleg löschen"
        message="Beleg wirklich löschen?"
        confirmLabel="Löschen"
        danger
        loading={deleteMutation.isPending}
        onConfirm={deleteReceipt}
      />
    </>
  );
}
